import React, { useState } from 'react';
import { api } from '../services/api';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import LocationMap from '../components/LocationMap';

export default function Onboarding() {
    const { dbUser, currentUser, loginWithPhone, linkPhoneToUser, setupRecaptcha, logout } = useAuth();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);

    // Phone Verification State
    const [otp, setOtp] = useState('');
    const [showOtpInput, setShowOtpInput] = useState(false);
    const [confirmationResult, setConfirmationResult] = useState(null);
    const [isPhoneVerified, setIsPhoneVerified] = useState(true); // DEV: Auto-verified to bypass reCAPTCHA issues
    const [verifying, setVerifying] = useState(false);

    const [formData, setFormData] = useState({
        name: '',
        type: 'RESTAURANT',
        address: {
            line1: '',
            city: '',
            zip: '',
            lat: '',
            lng: ''
        },
        contactInfo: {
            phone: '',
            email: ''
        }
    });

    // Auto-fill Email and Phone if available
    React.useEffect(() => {
        if (currentUser) {
            setFormData(prev => ({
                ...prev,
                contactInfo: {
                    ...prev.contactInfo,
                    email: currentUser.email || '',
                    phone: currentUser.phoneNumber || prev.contactInfo.phone
                }
            }));
            if (currentUser.phoneNumber) {
                setIsPhoneVerified(true);
            }
        }
    }, [currentUser]);

    // Redirect if already has restaurant admin role (basic check)
    React.useEffect(() => {
        if (dbUser && dbUser.roles) {
            const hasAdminRole = dbUser.roles.includes('RESTAURANT_ADMIN') ||
                dbUser.roles.includes('SUPER_ADMIN');
            if (hasAdminRole && !dbUser.isNewUser) {
                navigate('/dashboard');
            }
        }
    }, [dbUser, navigate]);

    const handleChange = (e) => {
        const { name, value } = e.target;

        if (name === 'contactInfo.phone') {
            setIsPhoneVerified(false);
            setShowOtpInput(false);
        }

        if (name.includes('.')) {
            const [parent, child] = name.split('.');
            setFormData(prev => ({
                ...prev,
                [parent]: {
                    ...prev[parent],
                    [child]: value
                }
            }));
        } else {
            setFormData(prev => ({ ...prev, [name]: value }));
        }
    };

    const handleDetectLocation = () => {
        if (!navigator.geolocation) {
            alert('Geolocation is not supported by your browser');
            return;
        }

        setLoading(true);
        navigator.geolocation.getCurrentPosition(
            (position) => {
                setFormData(prev => ({
                    ...prev,
                    address: {
                        ...prev.address,
                        lat: position.coords.latitude.toString(),
                        lng: position.coords.longitude.toString()
                    }
                }));
                setLoading(false);
                alert('Location detected successfully!');
            },
            (error) => {
                console.error('Error detecting location:', error);
                alert('Unable to retrieve your location. Please enter manually.');
                setLoading(false);
            }
        );
    };

    const handleSendOtp = async (e) => {
        e.preventDefault();
        if (!formData.contactInfo.phone) return alert("Please enter a phone number");

        setVerifying(true);
        try {
            console.log('[Onboarding] Setting up reCAPTCHA...');
            // Set to true to use visible reCAPTCHA - more reliable than invisible mode
            const useVisibleRecaptcha = true;
            setupRecaptcha('recaptcha-container-onboard', useVisibleRecaptcha);

            console.log('[Onboarding] Sending OTP to:', formData.contactInfo.phone);
            const appVerifier = window.recaptchaVerifier;
            const confirmation = await loginWithPhone(formData.contactInfo.phone, appVerifier);
            setConfirmationResult(confirmation);
            setShowOtpInput(true);
            alert("OTP Sent! Check your phone.");
        } catch (error) {
            console.error('[Onboarding] Error sending OTP:', error);

            if (error.code === 'auth/operation-not-allowed') {
                alert("Error: Phone Auth is disabled. Enable it in Firebase Console.");
            } else if (error.code === 'auth/too-many-requests') {
                alert("Too many requests. Please try again later.");
            } else if (error.message.includes('reCAPTCHA')) {
                alert("reCAPTCHA Error: " + error.message + "\n\nTry refreshing the page.");
            } else {
                alert("Failed to send OTP: " + (error.message || 'Unknown error'));
            }
        }
        setVerifying(false);
    };

    const handleVerifyOtp = async () => {
        setVerifying(true);
        try {
            console.log('[Onboarding] Verifying OTP...');
            // Link the phone number to the current user so we don't switch sessions (which clears email)
            await linkPhoneToUser(currentUser, confirmationResult, otp);
            setIsPhoneVerified(true);
            setShowOtpInput(false);
            alert("Phone Verified & Linked!");
        } catch (error) {
            console.error('[Onboarding] OTP Verification Error:', error);

            if (error.code === 'auth/invalid-verification-code') {
                alert("Invalid OTP. Please check and try again.");
            } else if (error.code === 'auth/code-expired') {
                alert("OTP has expired. Please request a new one.");
            } else {
                alert("Invalid OTP or Link Error: " + (error.message || 'Unknown error'));
            }
        }
        setVerifying(false);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        // DEV: Phone verification bypassed
        // if (!isPhoneVerified) {
        //     return alert("Please verify your phone number first.");
        // }

        setLoading(true);
        try {
            // Ensure we have a backend token - if not, get Firebase ID token
            let token = localStorage.getItem('backendToken');
            if (!token && currentUser) {
                console.log("No backend token, getting Firebase ID token...");
                const idToken = await currentUser.getIdToken();
                // Try to login to backend to get a proper token
                try {
                    const res = await api.post('/auth/login', { idToken });
                    token = res.data.token;
                    localStorage.setItem('backendToken', token);
                } catch (loginError) {
                    console.error("Backend login failed:", loginError);
                    // If backend login fails, we'll let the onboarding request fail with 401
                }
            }

            await api.post('/onboarding/restaurant', formData);

            // Refresh user data to get updated roles (now includes RESTAURANT_ADMIN)
            try {
                const userRes = await api.get('/auth/me');
                if (userRes.data.user) {
                    // Update dbUser with fresh data including new role
                    window.location.href = '/dashboard'; // Force full page reload to refresh context
                    return;
                }
            } catch (refreshError) {
                console.error("Failed to refresh user data:", refreshError);
            }

            alert('Restaurant Registered Successfully!');
            navigate('/dashboard');
        } catch (error) {
            console.error(error);
            alert('Failed to register: ' + (error.response?.data?.error || error.message));
        }
        setLoading(false);
    };

    return (
        <div style={styles.container}>
            <form onSubmit={handleSubmit} style={styles.form}>
                <h2>Register your Restaurant</h2>

                <div style={styles.group}>
                    <label>Restaurant Name</label>
                    <input name="name" value={formData.name} onChange={handleChange} required style={styles.input} />
                </div>

                <div style={styles.group}>
                    <label>Type</label>
                    <select name="type" value={formData.type} onChange={handleChange} style={styles.input}>
                        <option value="RESTAURANT">Restaurant</option>
                        <option value="CLOUD_KITCHEN">Cloud Kitchen</option>
                        <option value="FOOD_CART">Food Cart</option>
                        <option value="POP_UP">Pop Up</option>
                    </select>
                </div>

                <h3>Address</h3>
                <div style={styles.group}>
                    <input name="address.line1" placeholder="Address Line 1" value={formData.address.line1} onChange={handleChange} required style={styles.input} />
                </div>
                <div style={styles.row}>
                    <input name="address.city" placeholder="City" value={formData.address.city} onChange={handleChange} required style={{ ...styles.input, flex: 1, marginRight: '10px' }} />
                    <input name="address.zip" placeholder="Zip Code" value={formData.address.zip} onChange={handleChange} required style={{ ...styles.input, flex: 1 }} />
                </div>

                <div style={styles.group}>
                    <label>Location Coordinates (Drag pin to adjust)</label>
                    <div style={{ marginBottom: '10px' }}>
                        <LocationMap
                            lat={formData.address.lat}
                            lng={formData.address.lng}
                            onChange={(lat, lng) => {
                                setFormData(prev => ({
                                    ...prev,
                                    address: {
                                        ...prev.address,
                                        lat: lat.toString(),
                                        lng: lng.toString()
                                    }
                                }));
                            }}
                        />
                    </div>

                    <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-end' }}>
                        <div style={{ flex: 1 }}>
                            <label style={{ fontSize: '0.8rem', display: 'block', marginBottom: '4px' }}>Latitude</label>
                            <input
                                name="address.lat"
                                placeholder="Latitude"
                                value={formData.address.lat}
                                onChange={handleChange}
                                style={styles.input}
                            />
                        </div>
                        <div style={{ flex: 1 }}>
                            <label style={{ fontSize: '0.8rem', display: 'block', marginBottom: '4px' }}>Longitude</label>
                            <input
                                name="address.lng"
                                placeholder="Longitude"
                                value={formData.address.lng}
                                onChange={handleChange}
                                style={styles.input}
                            />
                        </div>
                        <button
                            type="button"
                            onClick={handleDetectLocation}
                            style={{ ...styles.detectBtn, height: '42px' }}
                            title="Detect current location"
                        >
                            📍 Detect
                        </button>
                    </div>
                </div>

                <h3>Contact Details</h3>
                <div style={styles.group}>
                    <label>Email</label>
                    <input
                        name="contactInfo.email"
                        placeholder="Email"
                        value={formData.contactInfo.email}
                        onChange={handleChange}
                        required
                        style={styles.input}
                        disabled={!!currentUser?.email}
                    />
                </div>

                <div style={styles.group}>
                    <label>Phone Number</label>
                    <div style={styles.phoneRow}>
                        <input
                            name="contactInfo.phone"
                            placeholder="+1 555 555 5555"
                            value={formData.contactInfo.phone}
                            onChange={handleChange}
                            required
                            disabled={false}
                            style={{ ...styles.input, flex: 1 }}
                        />
                        {!isPhoneVerified && !showOtpInput && (
                            <button type="button" onClick={handleSendOtp} disabled={verifying} style={styles.verifyButton}>
                                {verifying ? 'Sending...' : 'Verify'}
                            </button>
                        )}
                    </div>
                    <div id="recaptcha-container-onboard"></div>
                </div>

                {showOtpInput && (
                    <div style={styles.group}>
                        <label>Enter OTP</label>
                        <div style={styles.phoneRow}>
                            <input
                                value={otp}
                                onChange={(e) => setOtp(e.target.value)}
                                placeholder="123456"
                                style={{ ...styles.input, flex: 1 }}
                            />
                            <button type="button" onClick={handleVerifyOtp} disabled={verifying} style={styles.verifyButton}>
                                {verifying ? 'Verifying...' : 'Confirm'}
                            </button>
                        </div>
                    </div>
                )}

                {isPhoneVerified && <p style={{ color: 'green', fontSize: '0.9rem' }}>✓ Phone Verified</p>}

                <button type="submit" disabled={loading} style={styles.button}>
                    {loading ? 'Registering...' : 'Register Restaurant'}
                </button>
            </form>
        </div>
    );
}

const styles = {
    container: {
        maxWidth: '600px',
        margin: '2rem auto',
        padding: '2rem',
        backgroundColor: 'white',
        borderRadius: '8px',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
    },
    form: {
        display: 'flex',
        flexDirection: 'column',
        gap: '1rem'
    },
    group: {
        display: 'flex',
        flexDirection: 'column',
        gap: '0.5rem'
    },
    row: {
        display: 'flex',
        justifyContent: 'space-between'
    },
    input: {
        padding: '0.75rem',
        borderRadius: '4px',
        border: '1px solid #ddd',
        fontSize: '1rem'
    },
    button: {
        padding: '1rem',
        backgroundColor: '#0F9D58',
        color: 'white',
        border: 'none',
        borderRadius: '4px',
        fontSize: '1.1rem',
        cursor: 'pointer',
        marginTop: '1rem'
    },
    phoneRow: {
        display: 'flex',
        gap: '10px'
    },
    verifyButton: {
        padding: '0.75rem',
        backgroundColor: '#4285F4',
        color: 'white',
        border: 'none',
        borderRadius: '4px',
        cursor: 'pointer'
    },
    header: {
        display: 'flex',
        justifyContent: 'flex-end',
        marginBottom: '1rem'
    },
    logoutButton: {
        padding: '0.5rem 1rem',
        backgroundColor: '#f44336',
        color: 'white',
        border: 'none',
        borderRadius: '4px',
        cursor: 'pointer',
        fontSize: '0.9rem'
    },
    detectBtn: {
        padding: '4px 10px',
        backgroundColor: '#6C5CE7',
        color: 'white',
        border: 'none',
        borderRadius: '4px',
        cursor: 'pointer',
        fontSize: '0.8rem',
        fontWeight: 'bold'
    }
};
